<template>
  <cx-modal id="geo-info-modal"
    v-show="geoInfo.loaded" 
    v-on:dismiss="closeGeoInfo"
    v-trap-focus="{ escFunc: closeGeoInfo, live: true }">
    <div v-show="!hasGeoInfo">
      <cx-img 
        tag="i"
        class="m-graphic--ngi"
        imgSrc="/static/graphic/mgto_graphic_location.png"
      ></cx-img>
      <div>{{$t('sightMap.ngi')}}</div>
    </div>
    <div v-show="hasGeoInfo" class="geo-info-modal__wapper">
      <div
        v-if="disableMapbox"
        class="geo-info-modal__map geo-info-modal__map-placeholder"
        aria-hidden="true"
      >
        <cx-img
          tag="i"
          class="m-graphic--ngi"
          imgSrc="/static/graphic/mgto_graphic_location.png"
        ></cx-img>
      </div>
      <div v-else ref="map" class="geo-info-modal__map"></div>
      <div class="geo-info-modal__content"
        v-show="geoInfo.name && geoInfo.description"
        :style="{overflow: expandDesc ? 'auto': 'hidden'}"
        :class="{'-full': expandDesc}"
      >
        <h2 v-if="geoInfo.name">{{geoInfo.name}}</h2>
        <div class="cx-div" v-show="shortDesc && !expandDesc">{{shortDesc}}</div>
        <div class="cx-div" v-show="description && expandDesc" v-html="description"></div>
        <div class="geo-info-modal__expand-btn -center" v-if="description">
          <a class="cx-link color--midgrey" @click="expandDesc = !expandDesc">
            <i :class="`fas fa-caret-circle-${expandDesc ? 'up' : 'down'}`"></i>
          </a>
        </div>
      </div>
    </div>
  </cx-modal>
</template>

<script>
let mbgl;
if (process.env.VUE_ENV !== 'server') {
  mbgl = require('mapbox-gl');
}
import { mapState } from 'vuex';
import mapHelper from '@/mixins/mapHelper';
import autoFocus from '../mixins/autoFocus';

import { bounds } from './SightMapMapContainer.vue';

export default {
  name: 'geo-info-modal',
  mixins: [mapHelper, autoFocus],
  uit() {
    return [
      ['sightMap', ['ngi']]
    ]
  },
  data() {
    return {
      map: null,
      expandDesc: false,
      geoInfo: {
        loaded: false
      },
      recoverFocusElm: null
    }
  },
  computed: {
    ...mapState({
      isMobileView: state => state.ui.isMobileView,
      disableMapbox: state => state.ui.disableMapbox
    }),
    hasGeoInfo: function() {
      return this.sightHasGI(this.geoInfo);
    },
    shortDesc: function() {
      let shortDesc = this.geoInfo.description || '';
      return this.parseHtmlToShortDesc(shortDesc, this.isMobileView ? 60 : 120);
    },
    description: function() {
      return this.geoInfo.description || '';
    }
  },
  mounted() {
    const vm = this;
    if (this.disableMapbox) {
      window.GeoInfoModal = {
        open: this.updatedGeoInfo,
        close: this.closeGeoInfo
      }
      return;
    }
    mbgl.accessToken = this.mgtoMapAccessToken;
    window.mapboxgl = mbgl;
    window.mapboxgl.mgtoMapStyle = this.mgtoMapStyle;
    window.mapboxgl.mgtoCreateMarker = this._mgtoCreateMarker;
    window.mapboxgl.switchMapLocale = this.switchMapLocale;
    window.mapboxgl.mgtoFlyTo = this.flyTo;
    window.mapboxgl.mgtoInitMap = function(options) {
      const map = new mbgl.Map({
        center: [113.54982521363604,22.189467888922948],
        zoom: 15,
        maxBounds: bounds,
        style: window.mapboxgl.mgtoMapStyle,
        trackResize: true,
        localIdeographFontFamily: '"Mark Offc Pro Regular", "Lato Bold", "Mark Offc Pro Medium", "Mark Offc Pro Italic", "Noto Sans", "Arial Unicode MS Regular", sans-serif ',
        ...options
      });

      map.on('style.load', () => {
        window.mapboxgl.switchMapLocale({map});
        vm.blackAndWhite && vm.switchMapToGrayscale({map});
      });
      return map;
    }
    window.GeoInfoModal = {
      open: this.updatedGeoInfo,
      close: this.closeGeoInfo
    }
  },
  beforeDestroy() {
    window.GeoInfoModal = null;
  },
  methods: {
    updatedGeoInfo: function(info) {
      this.geoInfo = {
        ...info,
        loaded: true
      };
      setTimeout(this._af_autoFocus, 0);
      this.recoverFocusElm = document.activeElement;
    },
    closeGeoInfo: function() {
      this.geoInfo.loaded = false;
      this.expandDesc = false;
      this.recoverFocusElm && this.recoverFocusElm.focus();
    },
    bindMap: function(cb) {
      if (this.disableMapbox) return;
      if (!this.$refs.map) {
        console.warn('Try to bind map before mounted!');
        return ;
      }
      this.map = new mbgl.Map({
        container: this.$refs.map,
        style: this.mgtoMapStyle,
        center: [113.510351, 22.175949],
        zoom: 12,
        maxBounds: bounds,
        trackResize: true,
        localIdeographFontFamily: '"Mark Offc Pro Regular", "Lato Bold", "Mark Offc Pro Medium", "Mark Offc Pro Italic", "Noto Sans", "Arial Unicode MS Regular", sans-serif ',
      });

      this.map.on('style.load', () => {
        this.switchMapLocale();
        this.blackAndWhite && this.switchMapToGrayscale();
      });

      if (typeof cb == 'function') {
        this.$nextTick(cb)
      }
    },
    projectGeoInfo: function() {
      if (this.disableMapbox) return;
      const projectID = 'geo-info-modal' + new Date().getTime();
      this.resizeMap();
      this.flyToSight({
        sight: this.geoInfo,
      });
      this.projectSightsAsMarker({
        projectID,
        sights: [{
          ...this.geoInfo,
          id: this.geoInfo.name
        }],
        startIdx: -1,
        options: {
          addDescToPopup: true
        }
      });
      setTimeout(() => {
        this.activeProjectedMarker({id: 0}); 
        this.projectPOIS({
          projectID,
          pois: this.geoInfo.pois
        })
      }, 0)
    },
  },
  watch: {
    geoInfo: {
      deep: true,
      immediate: true,
      handler: function() {
        if (!this.hasGeoInfo) return ;

        if (this.map == null) {
          this.bindMap(this.projectGeoInfo);
        } else {
          this.$nextTick(this.projectGeoInfo);
        }
      }
    },
    '$route.path': function() {
      this.closeGeoInfo();
    }
  }
}
</script>

<style lang="scss">
.geo-info-modal{
  $r: &;
  &__expand-btn {
    position: absolute;
    // bottom: $space-base;
    width: 100%;
    left: 0;
  }
        
  &__content {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    color: $color-white;
    min-height: 5%;
    max-height: 100%;
    overflow: scroll;
    // transition: all .2s ease-in-out;
    padding: $space-base;
    padding-bottom: $space-lg;
    background-image: linear-gradient(
      to bottom, 
      rgba($color-black, .8), 
      rgba($color-black, .0)
    );
    text-align: left;
    &.-full{
      min-height: 100%;
      background-image: linear-gradient(
        to bottom, 
        rgba($color-black, .8), 
        rgba($color-black, .8)
      );
    }
    &:not(.-full) {
      pointer-events: none;
    }

    & .geo-info-modal__expand-btn .cx-link {
      pointer-events: auto;
    }
  }
  &__map {
    text-align: initial;
    height: 90vh;
    max-height: 800px;
    width: 100%;
    .mapboxgl-canvas{
      position: relative !important;
    }
  }
  &__map-placeholder {
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgb(238, 238, 238);
  }
}
</style>
